import {ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal} from '@angular/core';
import {MatTabChangeEvent, MatTabsModule} from "@angular/material/tabs";
import {FormGroup} from "@angular/forms";
import {RecordData, SelectData} from "../../../../../types/records-table.types";
import {contactFormFields} from "../../configs/contacts-modal-config";
import {ContactFormFields, ContactsDialogData} from "../../types/contacts-modal.types";
import {FormContactsModalComponent} from "../form-contacts-modal/form-contacts-modal.component";
import {MatButton} from "@angular/material/button";
import {BaseModalService} from "../../../services/base-modal.service";
import {ContactsModalService} from "../../services/contacts-modal.service";
import {CloseModalReason} from "../../../../../types/modal.types";
import {defaultRecordData} from "../../../../../models/default-record-data";
import {AttachProps} from "../../../../../types/attach-detach.types";
import {CreateBankDetailsParams} from "../../../bank-details-modal/types/bank-details-modal.types";
import {MatExpansionModule} from "@angular/material/expansion";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-new-contacts-modal',
  standalone: true,
  imports: [
    MatTabsModule,
    FormContactsModalComponent,
    MatButton,
    MatExpansionModule,
    LoaderComponent
  ],
  templateUrl: './new-contacts-modal.component.html',
  styleUrls: [
    './new-contacts-modal.component.scss',
    '../../contacts-modal.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewContactsModalComponent implements OnInit {
  contactsDialogData = input.required<ContactsDialogData>();
  telegramAccountIds = input.required<Array<SelectData>>();
  saveButtonClick = output<number>();
  closeDialog = output<CloseModalReason>();

  private baseModalService: BaseModalService = inject(BaseModalService);
  private contactsModalService: ContactsModalService = inject(ContactsModalService);

  public selectedTabIndex = signal<number>(0);
  public contactsData = signal<Array<RecordData>>([]);
  public contactFormGroups = signal<Array<FormGroup>>([]);
  public isContactFormValid = signal<boolean>(false);
  public recordCalendarId = computed(() => this.baseModalService.getRecordCalendarId(this.contactsDialogData()?.recordData || defaultRecordData));
  public bloggerId = computed(() => this.contactsDialogData()?.bloggerId || 0);

  public async ngOnInit(): Promise<void> {
    await this.fetchContactsData();
  }

  public handleTabChange(event: MatTabChangeEvent): void {
    this.contactFormGroups().at(0)?.reset();
    this.selectedTabIndex.set(event.index);
  }

  public getCurrentContactTab(): string {
    const count = this.contactsData().length || '-';
    return `Выбрать из существующих (${count})`;
  }

  public async handleSaveButtonClick(index: number): Promise<void> {
    const id = index === - 1 ?
      await this.createContactData() :
      await this.attachContactData(index);

    this.saveButtonClick.emit(id);
  }

  private async fetchContactsData(): Promise<void> {
    const data = await this.contactsModalService.fetchContactsData(this.bloggerId())
      .catch(() => []);
    this.contactsData.set(data);
    const emptyFormGroup = this.baseModalService.getFormGroup<ContactFormFields>(contactFormFields);
    const formGroups = this.contactsData().map(data => {
      const formGroup = this.baseModalService.getFormGroup<ContactFormFields>(contactFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });

    this.contactFormGroups.set([
      emptyFormGroup,
      ...formGroups
    ]);
    this.isContactFormValid.set(true);
  }

  private async attachContactData(index: number): Promise<number> {
    const contactId = Number(this.contactsData().at(index)?.id);
    const attachProps: AttachProps = this.contactsModalService.createAttachProps(this.recordCalendarId(), contactId);
    await this.contactsModalService.attachData(attachProps)
      .catch(() => this.closeDialog.emit('cancel'));

    return contactId;
  }

  private async createContactData(): Promise<number> {
    const createContactParams: CreateBankDetailsParams = {
      recordData: {
        ...this.contactFormGroups().at(0)?.value,
        bloggerId: this.bloggerId()
      },
      recordCalendarId: this.recordCalendarId()
    };

    const data = await this.contactsModalService.createContactData(createContactParams)
      .catch(() => defaultRecordData);

    return Number(data.id)
  }
}
